import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, ArrowRight, Check, Loader2, AlertCircle } from 'lucide-react';
import { Seo } from '../components/Seo';
import { useAuth } from '../contexts/AuthContext';
import { usePricingCatalog } from '../hooks/usePricingCatalog';
import { getSizeLimit } from './QuoteCalculator/quoteConstants';
import { lookupPricingAlias } from '../data/pricingTaxonomyAliases';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Standard quantity ladders — matches the wizard + admin price tables.
// Roll-format products honour a 250-min ladder; everything else gets
// the 50-base ladder so customers see a meaningful low-volume entry
// price.
const QTY_TIERS_GENERIC = [50, 100, 250, 500, 1000, 2500, 5000];
const QTY_TIERS_ROLLS = [250, 500, 1000, 2500, 5000];

const isRollsFormat = (fmt) => typeof fmt === 'string' && fmt.toLowerCase().includes('roll');

/**
 * StageTag — numbered chevron-tag label for the calculator's three
 * stages. Visual language matches the `JourneyStepper` arrow-tag
 * style so the calculator feels native to the rest of the wizard.
 */
const StageTag = ({ number, label }) => (
  <div
    className="inline-flex items-center gap-3 pl-4 pr-7 py-3 bg-[#E9F2EA] text-[#14281D] shadow-sm"
    style={{
      clipPath: 'polygon(0 0, calc(100% - 14px) 0, 100% 50%, calc(100% - 14px) 100%, 0 100%)',
    }}
    data-testid={`pricing-stage-${number}`}
  >
    <span className="flex-shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#6FBF73] text-white font-bold text-sm">
      {number}
    </span>
    <span className="text-sm md:text-base font-semibold whitespace-nowrap">{label}</span>
  </div>
);

/**
 * Live-pricing calculator for `/pricing-catalog`. Replaces the
 * previous long scroll-list of static price tables with a focused
 * three-stage form (product → size → quantity) that resolves to a
 * real-time price ladder via `POST /api/pricing/calculate`.
 *
 * iter114s.51 — "current layout has the user scrolling down to find
 * different products and appears way too busy. Reference attached
 * (Choose a Product) and create a drop-down menu selection that
 * showcases live pricing directly underneath as the sample shown."
 */
export const PricingCatalog = () => {
  const { isAuthenticated, pricingTier } = useAuth();
  const navigate = useNavigate();

  const [category, setCategory] = useState('');
  const [format, setFormat] = useState('');
  const [finish, setFinish] = useState('');
  const [unit, setUnit] = useState('inches');
  const [width, setWidth] = useState('2');
  const [height, setHeight] = useState('2');
  const [selectedQty, setSelectedQty] = useState(null);
  const [currency, setCurrency] = useState('CAD');

  // Single fetch — pull the full catalog ONCE and derive the
  // cascading dropdowns client-side. The previous cascading-fetch
  // approach caused a rapid setState/refetch fight (auto-select
  // category → triggers refetch → narrows formats → auto-select
  // format → triggers refetch → ...) that resulted in cancelled
  // requests in StrictMode and empty dropdowns.
  const { options: allOptions, loading: catalogLoading, error: catalogError } =
    usePricingCatalog({});

  const categories = useMemo(
    () => Array.from(new Set(allOptions.map((o) => o.category))).sort(),
    [allOptions],
  );
  const formats = useMemo(
    () => {
      if (!category) return [];
      return Array.from(
        new Set(allOptions.filter((o) => o.category === category).map((o) => o.format)),
      ).sort();
    },
    [allOptions, category],
  );
  const finishes = useMemo(
    () => {
      if (!category || !format) return [];
      return Array.from(
        new Set(
          allOptions
            .filter((o) => o.category === category && o.format === format)
            .map((o) => o.finish),
        ),
      ).sort();
    },
    [allOptions, category, format],
  );

  // Auto-select the first category when the page loads (so the
  // calculator never sits on an empty state).
  useEffect(() => {
    if (!category && categories.length > 0) {
      setCategory(categories[0]);
    }
  }, [categories, category]);

  // Auto-select the first format whenever the available formats list
  // narrows (i.e. the customer picked a different category).
  useEffect(() => {
    if (formats.length > 0 && !formats.includes(format)) {
      setFormat(formats[0]);
    }
  }, [formats, format]);

  // Auto-select the first finish whenever the format changes.
  useEffect(() => {
    if (finishes.length > 0 && !finishes.includes(finish)) {
      setFinish(finishes[0]);
    }
  }, [finishes, finish]);

  // Resolved combo for the currently selected (cat, fmt, fin).
  const resolvedCombo = useMemo(
    () => allOptions.find((o) => o.category === category && o.format === format && o.finish === finish),
    [allOptions, category, format, finish],
  );

  // iter114s.59 — Resolve the current pricing-taxonomy triple to the
  // wizard's productData ids via the alias map, then look up the
  // production max W×H limit. The Pricing Catalog now applies the same
  // dimension restrictions the QuoteCalculator already enforces, so a
  // customer typing 99×99 in for a 6×6 Patches order will see the
  // value snap to 6×6 with a friendly inline note instead of being
  // quoted a price the production line can't actually run. The limit
  // is INCHES from `SIZE_LIMITS` — when the customer is using cm we
  // translate before clamping.
  const sizeLimit = useMemo(() => {
    if (!category || !format || !finish) return null;
    const alias = lookupPricingAlias(category, format, finish);
    if (!alias) return null;
    return getSizeLimit(alias.product, alias.format);
  }, [category, format, finish]);

  const sizeLimitDisplay = useMemo(() => {
    if (!sizeLimit) return null;
    if (unit === 'centimeters') {
      return {
        maxW: Math.floor(sizeLimit.maxW * 2.54 * 10) / 10,
        maxH: Math.floor(sizeLimit.maxH * 2.54 * 10) / 10,
      };
    }
    return { maxW: sizeLimit.maxW, maxH: sizeLimit.maxH };
  }, [sizeLimit, unit]);

  const [clampNote, setClampNote] = useState(null);
  // Clamp on blur — leaving an over-max value in the input but rolling
  // it back the moment the customer commits gives them a moment to
  // self-correct without feeling fought (vs clamping on every keystroke
  // which would prevent them ever typing a multi-digit value).
  const clampDimension = (raw, axis) => {
    if (!sizeLimitDisplay) return raw;
    const v = parseFloat(raw);
    if (!Number.isFinite(v) || v <= 0) return raw;
    const max = axis === 'width' ? sizeLimitDisplay.maxW : sizeLimitDisplay.maxH;
    if (v > max) {
      setClampNote({ axis, requested: v, max });
      return String(max);
    }
    return raw;
  };

  const handleWidthBlur = () => setWidth((w) => clampDimension(w, 'width'));
  const handleHeightBlur = () => setHeight((h) => clampDimension(h, 'height'));

  // Clear the clamp-notice once the customer changes anything else
  // (different product, switched units, etc.) so the message doesn't
  // linger after it's no longer relevant.
  useEffect(() => {
    setClampNote(null);
  }, [category, format, finish, unit]);

  // Quantity tiers depend on whether the format is a Roll product.
  const qtyTiers = useMemo(
    () => (isRollsFormat(format) ? QTY_TIERS_ROLLS : QTY_TIERS_GENERIC),
    [format],
  );

  // ---- Live price computation ----
  // Whenever (combo, size) changes, fan out a /pricing/calculate call
  // for every quantity tier in parallel and stash the matrix locally.
  // Cancelable via a ref so a fast user changing dimensions doesn't
  // cause an out-of-order overwrite.
  const [priceList, setPriceList] = useState([]);
  const [priceLoading, setPriceLoading] = useState(false);
  const [priceError, setPriceError] = useState(null);
  const fetchIdRef = useRef(0);

  useEffect(() => {
    if (!category || !format || !finish) {
      setPriceList([]);
      return;
    }
    const w = parseFloat(width);
    const h = parseFloat(height);
    if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) {
      setPriceList([]);
      return;
    }
    const myFetch = ++fetchIdRef.current;
    setPriceLoading(true);
    setPriceError(null);

    // Convert cm → in if the customer is using metric (the backend
    // pricing matrix is inches-only).
    const widthIn = unit === 'centimeters' ? w / 2.54 : w;
    const heightIn = unit === 'centimeters' ? h / 2.54 : h;

    Promise.all(
      qtyTiers.map((q) =>
        axios
          .post(`${API_URL}/api/pricing/calculate`, {
            category,
            format,
            finish,
            width: widthIn,
            height: heightIn,
            quantity: q,
          })
          .then((r) => ({ qty: q, ok: true, ...r.data }))
          .catch((err) => ({
            qty: q,
            ok: false,
            error: err?.response?.data?.detail?.message || err?.response?.data?.detail || err.message,
            requiresCustomQuote: !!err?.response?.data?.detail?.requires_custom_quote,
          })),
      ),
    ).then((rows) => {
      if (myFetch !== fetchIdRef.current) return; // stale response, abort
      setPriceList(rows);
      setPriceLoading(false);
      // Auto-select smallest qty whose price resolved successfully so
      // the customer always sees an active row + total.
      const firstOk = rows.find((r) => r.ok);
      if (firstOk && (!selectedQty || !rows.some((r) => r.qty === selectedQty && r.ok))) {
        setSelectedQty(firstOk.qty);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, format, finish, width, height, unit, qtyTiers]);

  // Derive savings % for each row vs the cheapest-per-unit baseline
  // (smallest tier). Matches the reference UI's "-12%, -25%" badges.
  const rowsWithDiscount = useMemo(() => {
    if (!priceList.length) return [];
    const baseline = priceList.find((r) => r.ok);
    if (!baseline) return priceList.map((r) => ({ ...r, discount: 0 }));
    const baselinePerUnit = baseline.cad / baseline.qty;
    return priceList.map((r) => {
      if (!r.ok) return { ...r, discount: 0 };
      const perUnit = r.cad / r.qty;
      const discount = Math.max(0, Math.round((1 - perUnit / baselinePerUnit) * 100));
      return { ...r, discount };
    });
  }, [priceList]);

  const selectedRow = useMemo(
    () => rowsWithDiscount.find((r) => r.qty === selectedQty),
    [rowsWithDiscount, selectedQty],
  );

  const formatPrice = (val) => {
    if (val === null || val === undefined) return '—';
    return `$${val.toFixed(2)}`;
  };

  // ---- Custom-quantity handler ----
  // Forward the resolved (category, format, finish) display names
  // (sans quantity) to the Quote Calculator so the customer can pick
  // a quantity outside the standard ladder.
  //
  // iter114s.59 — `handleContinue` removed alongside the "Add to Cart
  // & Continue" footer. Customers now route to the Quote Calculator
  // via either a per-row "Custom Size / Qty" link or the
  // informational footer's "Get a Custom Quote" call-to-action; both
  // forward the resolved category/format/finish/size/qty params so
  // the wizard pre-fills automatically.

  const handleCustomQuantity = () => {
    const params = new URLSearchParams({
      category,
      format,
      finish,
      w: width,
      h: height,
      u: unit,
    });
    navigate(`/quote?${params.toString()}`);
  };

  // Catalog loading state — show a friendly skeleton.
  if (catalogLoading && !categories.length) {
    return (
      <div className="min-h-screen bg-[#FAFCF7] flex items-center justify-center" data-testid="pricing-catalog-page">
        <div className="text-center text-gray-500">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3 text-[#6FBF73]" />
          Loading the live pricing catalog…
        </div>
      </div>
    );
  }

  if (catalogError) {
    return (
      <div className="min-h-screen bg-[#FAFCF7] p-10" data-testid="pricing-catalog-page">
        <div className="max-w-xl mx-auto bg-red-50 border border-red-200 text-red-700 rounded-lg p-4">
          {catalogError}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFCF7]" data-testid="pricing-catalog-page">
      <Seo
        title="Live Pricing Catalog — Custom Stickers & Labels | Evergreen Graphics Group"
        description="Browse live, transparent pricing for custom stickers, labels, decals, DTF transfers and more. Compare quantity breaks instantly — Canada & USA shipping."
        path="/pricing-catalog"
      />
      {/* HERO */}
      <section className="bg-gradient-to-br from-[#E8F1E6] via-[#F4F7F2] to-white border-b border-[#6FBF73]/30">
        <div className="max-w-5xl mx-auto px-6 py-10 md:py-14">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/80 backdrop-blur-sm border border-[#6FBF73]/40 text-[#2F5D3A] text-xs font-semibold mb-3">
            <Sparkles className="w-3.5 h-3.5" /> Live Pricing Calculator
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-[#14281D] tracking-tight leading-tight">
            Instant Online <span className="text-[#6FBF73]">Pricing</span>
          </h1>
          <p className="mt-3 text-sm md:text-base text-[#1F3D2C]/80 max-w-2xl">
            Pick your product, size, and quantity — see the price update in real time. Add to cart in one click.
            {isAuthenticated && pricingTier === 'evergreen' && (
              <span className="block mt-1 text-[#2F5D3A] font-semibold">
                Your Evergreen partner discount is already baked in.
              </span>
            )}
          </p>
        </div>
      </section>

      {/* CALCULATOR */}
      <main className="max-w-5xl mx-auto px-6 py-10">
        <div className="bg-white border border-[#E5E7EB] rounded-2xl shadow-sm overflow-hidden">
          {/* ============== STAGE 1: Product ============== */}
          <section className="p-6 md:p-8 border-b border-[#E5E7EB]">
            <div className="flex flex-col md:flex-row md:items-start gap-4 md:gap-8">
              <div className="md:w-48 shrink-0 pt-1">
                <StageTag number={1} label="Choose a Product" />
              </div>
              <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4">
                <label className="block">
                  <span className="block text-sm font-medium text-[#14281D] mb-1.5">Category</span>
                  <select
                    value={category}
                    onChange={(e) => {
                      setCategory(e.target.value);
                      setFormat('');
                      setFinish('');
                    }}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#6FBF73] focus:border-transparent"
                    data-testid="pricing-select-category"
                  >
                    {categories.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="block text-sm font-medium text-[#14281D] mb-1.5">Format</span>
                  <select
                    value={format}
                    onChange={(e) => {
                      setFormat(e.target.value);
                      setFinish('');
                    }}
                    disabled={!formats.length}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#6FBF73] focus:border-transparent disabled:bg-gray-50 disabled:text-gray-400"
                    data-testid="pricing-select-format"
                  >
                    {formats.map((f) => (
                      <option key={f} value={f}>{f}</option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="block text-sm font-medium text-[#14281D] mb-1.5">Finish</span>
                  <select
                    value={finish}
                    onChange={(e) => setFinish(e.target.value)}
                    disabled={!finishes.length}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#6FBF73] focus:border-transparent disabled:bg-gray-50 disabled:text-gray-400"
                    data-testid="pricing-select-finish"
                  >
                    {finishes.map((f) => (
                      <option key={f} value={f}>{f}</option>
                    ))}
                  </select>
                </label>
                {resolvedCombo && (
                  <div className="md:col-span-3 pt-1">
                    <span className="text-xs uppercase tracking-wide text-gray-500 mr-2">Product:</span>
                    <span className="text-sm text-[#2F5D3A] font-medium" data-testid="pricing-resolved-product">
                      {resolvedCombo.finish}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* ============== STAGE 2: Size ============== */}
          <section className="p-6 md:p-8 border-b border-[#E5E7EB] bg-[#FAFCF7]">
            <div className="flex flex-col md:flex-row md:items-start gap-4 md:gap-8">
              <div className="md:w-48 shrink-0 pt-1">
                <StageTag number={2} label="Choose a Size" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-6 mb-4">
                  {['inches', 'centimeters'].map((u) => (
                    <label key={u} className="inline-flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="pricing-unit"
                        value={u}
                        checked={unit === u}
                        onChange={() => setUnit(u)}
                        className="w-4 h-4 accent-[#6FBF73]"
                        data-testid={`pricing-unit-${u}`}
                      />
                      <span className="text-sm font-medium text-[#14281D] capitalize">{u}</span>
                    </label>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-4 max-w-md">
                  <label className="block">
                    <span className="block text-sm font-medium text-[#14281D] mb-1.5">
                      Width
                      {sizeLimitDisplay && (
                        <span className="ml-2 text-[10px] uppercase tracking-wide text-gray-400 font-normal">
                          max {sizeLimitDisplay.maxW}{unit === 'inches' ? '"' : ' cm'}
                        </span>
                      )}
                    </span>
                    <input
                      type="number"
                      min="0.1"
                      step="0.1"
                      max={sizeLimitDisplay?.maxW}
                      value={width}
                      onChange={(e) => setWidth(e.target.value)}
                      onBlur={handleWidthBlur}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#6FBF73] focus:border-transparent tabular-nums"
                      data-testid="pricing-input-width"
                    />
                  </label>
                  <label className="block">
                    <span className="block text-sm font-medium text-[#14281D] mb-1.5">
                      Height
                      {sizeLimitDisplay && (
                        <span className="ml-2 text-[10px] uppercase tracking-wide text-gray-400 font-normal">
                          max {sizeLimitDisplay.maxH}{unit === 'inches' ? '"' : ' cm'}
                        </span>
                      )}
                    </span>
                    <input
                      type="number"
                      min="0.1"
                      step="0.1"
                      max={sizeLimitDisplay?.maxH}
                      value={height}
                      onChange={(e) => setHeight(e.target.value)}
                      onBlur={handleHeightBlur}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#6FBF73] focus:border-transparent tabular-nums"
                      data-testid="pricing-input-height"
                    />
                  </label>
                </div>
                {clampNote && (
                  <div
                    className="mt-3 flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2"
                    data-testid="pricing-clamp-note"
                  >
                    <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                    <span>
                      <strong className="capitalize">{clampNote.axis}</strong> capped at{' '}
                      <strong className="tabular-nums">{clampNote.max}{unit === 'inches' ? '"' : ' cm'}</strong>{' '}
                      — this product can't be produced larger than that. For oversize jobs,{' '}
                      <Link to="/quote" className="underline font-semibold text-amber-800 hover:text-amber-900">
                        request a custom quote
                      </Link>
                      .
                    </span>
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* ============== STAGE 3: Quantity ============== */}
          <section className="p-6 md:p-8">
            <div className="flex flex-col md:flex-row md:items-start gap-4 md:gap-8">
              <div className="md:w-48 shrink-0 pt-1">
                <div className="flex items-center justify-between md:block">
                  <StageTag number={3} label="Choose a Quantity" />
                  <div className="md:mt-3 inline-flex rounded-full border border-[#6FBF73]/40 overflow-hidden text-xs">
                    {['CAD', 'USD'].map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setCurrency(c)}
                        className={`px-3 py-1 font-semibold transition-colors ${
                          currency === c ? 'bg-[#6FBF73] text-white' : 'bg-white text-[#2F5D3A] hover:bg-[#6FBF73]/10'
                        }`}
                        data-testid={`pricing-currency-${c.toLowerCase()}`}
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex-1">
                {priceLoading && !rowsWithDiscount.length && (
                  <div className="text-sm text-gray-500 flex items-center gap-2 py-6">
                    <Loader2 className="w-4 h-4 animate-spin text-[#6FBF73]" />
                    Calculating live prices…
                  </div>
                )}

                {priceError && (
                  <div className="text-red-700 bg-red-50 border border-red-200 p-3 rounded-lg text-sm">
                    {priceError}
                  </div>
                )}

                {rowsWithDiscount.length > 0 && (
                  <ul className="border border-[#E5E7EB] rounded-xl overflow-hidden divide-y divide-[#E5E7EB]" data-testid="pricing-quantity-list">
                    {rowsWithDiscount.map((row) => {
                      const isSelected = selectedQty === row.qty;
                      const priceField = currency === 'USD' ? 'usd' : 'cad';
                      const isOk = row.ok;
                      return (
                        <li key={row.qty}>
                          <button
                            type="button"
                            onClick={() => isOk && setSelectedQty(row.qty)}
                            disabled={!isOk}
                            className={`w-full flex items-center justify-between px-4 py-3.5 text-left transition-colors ${
                              isSelected
                                ? 'bg-[#E8F5E9] border-l-4 border-[#6FBF73]'
                                : isOk
                                  ? 'bg-white hover:bg-[#F4F7F2] border-l-4 border-transparent'
                                  : 'bg-gray-50 cursor-not-allowed border-l-4 border-transparent text-gray-400'
                            }`}
                            data-testid={`pricing-qty-row-${row.qty}`}
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <span className={`font-semibold text-base ${isSelected ? 'text-[#2F5D3A]' : 'text-[#14281D]'}`}>
                                {row.qty.toLocaleString()}
                              </span>
                              <span className="text-xs text-gray-500 hidden sm:inline">
                                {isRollsFormat(format) ? 'units / roll' : 'units'}
                              </span>
                              {!isOk && row.requiresCustomQuote && (
                                <span className="text-[10px] uppercase tracking-wide bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                                  Custom Quote
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-3 shrink-0">
                              {isOk && row.discount > 0 && (
                                <span
                                  className="inline-flex items-center px-2 py-0.5 rounded-md bg-[#E8F5E9] text-[#2F5D3A] text-xs font-bold tabular-nums"
                                  style={{
                                    clipPath: 'polygon(8px 0, 100% 0, 100% 100%, 8px 100%, 0 50%)',
                                    paddingLeft: '12px',
                                  }}
                                >
                                  -{row.discount}%
                                </span>
                              )}
                              <span className={`font-bold text-base tabular-nums ${isSelected ? 'text-[#2F5D3A]' : 'text-[#14281D]'}`}>
                                {isOk ? formatPrice(row[priceField]) : '—'}
                              </span>
                              {isSelected && <Check className="w-4 h-4 text-[#6FBF73]" />}
                            </div>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}

                <button
                  type="button"
                  onClick={handleCustomQuantity}
                  className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-[#4A7C59] hover:text-[#2F5D3A] hover:underline"
                  data-testid="pricing-custom-qty"
                >
                  Custom Quantity
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </section>

          {/* ============== Footer / Informational summary ==============
              iter114s.59 — Per operator brief, the Pricing Catalog
              footer is now informational only. "Add to Cart" + "Continue"
              were removed so the section stays a quotation/preview
              surface and doesn't prematurely push checkout actions.
              Customers convert to an order via the dedicated Quote
              Calculator (deep-linked from each row's "Custom Size /
              Qty" CTA or via the "Get a Custom Quote" button below). */}
          <footer className="bg-[#14281D] text-white">
            <div className="px-6 md:px-8 py-5 grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
              {/* Selection summary + per-unit price */}
              <div className="text-sm md:col-span-2" data-testid="pricing-footer-summary">
                {selectedRow && selectedRow.ok ? (
                  <>
                    <div className="text-xs uppercase tracking-wide text-emerald-200/70 mb-1">Your Quick Quote</div>
                    <div className="font-semibold tabular-nums text-base">
                      {selectedRow.qty.toLocaleString()} units · {width}×{height} {unit === 'inches' ? 'in' : 'cm'} ·{' '}
                      <span className="text-[#6FBF73]">
                        {formatPrice(currency === 'USD' ? selectedRow.usd : selectedRow.cad)} {currency}
                      </span>
                    </div>
                    <div className="text-xs text-emerald-200/80 mt-1 tabular-nums">
                      Your price per unit:{' '}
                      <span className="font-semibold text-white">
                        {formatPrice((currency === 'USD' ? selectedRow.usd : selectedRow.cad) / selectedRow.qty)}
                      </span>{' '}
                      {currency}
                    </div>
                    <div className="text-[11px] text-emerald-200/60 mt-2 leading-snug">
                      Taxes not included. Shipping rates calculated at checkout and exclude statutory holidays.
                    </div>
                  </>
                ) : (
                  <span className="text-emerald-200/70">Pick a product, size, and quantity to see your total.</span>
                )}
              </div>
              {/* Informational actions — Print + Custom Quote link */}
              <div className="flex flex-col gap-2 md:items-end">
                <button
                  type="button"
                  onClick={() => window.print()}
                  disabled={!selectedRow || !selectedRow.ok}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="pricing-print-cta"
                >
                  Print Quote · Save as PDF
                </button>
                <Link
                  to="/quote"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#6FBF73] hover:text-white transition-colors"
                  data-testid="pricing-custom-quote-link"
                >
                  Need more help? Get a Custom Quote
                  <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            </div>
          </footer>
        </div>

        {/* Secondary CTA strip — for customers whose product isn't in
            the live catalog (e.g. Specialty Niche Products, custom
            substrates, oversize jobs). */}
        <div className="mt-8 rounded-2xl bg-white border border-[#E5E7EB] p-6 text-center shadow-sm">
          <h2 className="text-lg font-semibold text-[#14281D] mb-1">Need something custom?</h2>
          <p className="text-sm text-gray-600 mb-4">
            Specialty finishes, oversize formats, exotic substrates — our team will quote within 24 hours.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <a
              href="mailto:info@eg3.ca"
              className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-[#6FBF73] text-[#2F5D3A] hover:bg-[#6FBF73] hover:text-white font-semibold text-sm transition-colors"
              data-testid="pricing-contact-cta"
            >
              Talk to a Specialist
            </a>
            <Link
              to="/products"
              className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#2F5D3A] hover:bg-[#14281D] text-white font-semibold text-sm transition-colors"
              data-testid="pricing-browse-products-cta"
            >
              Browse All Products
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PricingCatalog;
